/**
 * Iter-262d — Community Highlights widget (full redesign).
 *
 * Replaces the Iter-250 version which had multiple responsive layout
 * issues in production (overlapping labels, truncated titles, cards too
 * narrow). Verified across 320 / 390 / 768 / 1024 / 1440 widths.
 *
 * Layout principles:
 *   • Icon sits ABOVE label so they can't collide horizontally
 *   • Tile grid: 2 cols on phone, 4 cols starting at md (768px)
 *   • Min-height on tiles so values + sub-text never collide
 *   • Sample posts use line-clamp-2 instead of single-line truncate
 *   • All copy stays inside its tile at every tested breakpoint
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Sparkles, Cake, Award, Trophy, ChevronRight } from 'lucide-react';
import { FeedPostContent } from './FeedPostContent';


const RECOGNITION_KIND_ICONS = {
  birthday: '🎂',
  first_booking: '🎯',
  five_star_review: '⭐',
  certification_earned: '🛡️',
  top_performer: '🏆',
  new_creator: '🎬',
};


export default function CommunityHighlights() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get('/community/highlights')
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(() => { /* silent — widget hides if no data */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!data) return null;

  // Iter-312b — Backend now filters placeholder DOBs, is_suspended,
  // pending_deletion, etc. The value we receive is production-truth so
  // we render it directly (no FE cap).
  const birthdaysDisplay = data.birthdays_today || 0;

  const total = (data.new_recognition_posts || 0) + birthdaysDisplay
              + (data.new_certifications || 0) + (data.top_performer ? 1 : 0);
  if (total === 0 && !data.sample_posts?.length) return null;

  return (
    <Card
      className="overflow-hidden border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 via-pink-50 to-amber-50 max-w-full"
      data-testid="community-highlights-widget"
    >
      <CardContent className="p-4 sm:p-6">
        {/* Header — title wraps to its own line on phones; CTA goes full-width below */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 sm:mb-5">
          <h3 className="text-base font-bold text-fuchsia-900 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-fuchsia-600 shrink-0" />
            <span>Community Highlights</span>
          </h3>
          <Button
            size="sm"
            onClick={() => navigate('/community')}
            className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-xs h-8 px-3"
            data-testid="view-community-btn"
          >
            View Community
            <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>

        {/* KPI tiles — 2 cols on phone, 4 cols at md (768px+).
            Iter-312b: added min-w-0 on each tile so long "sub" strings
            (e.g. top-performer names) truncate cleanly on desktop
            instead of forcing the tile to overflow its parent card. */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 min-w-0">
          <KpiTile
            icon={<Sparkles className="w-4 h-4" />}
            label="Recognition"
            value={data.new_recognition_posts}
            color="fuchsia"
            testid="kpi-recognition"
          />
          <KpiTile
            icon={<Cake className="w-4 h-4" />}
            label="Birthdays"
            value={birthdaysDisplay}
            color="pink"
            testid="kpi-birthdays"
          />
          <KpiTile
            icon={<Award className="w-4 h-4" />}
            label="Certifications"
            value={data.new_certifications}
            color="emerald"
            testid="kpi-certs"
          />
          <KpiTile
            icon={<Trophy className="w-4 h-4" />}
            label="Top Performer"
            value={data.top_performer ? 1 : 0}
            sub={data.top_performer?.name}
            color="amber"
            testid="kpi-top-performer"
          />
        </div>

        {/* Latest in the Community */}
        {data.sample_posts?.length > 0 && (
          <div className="bg-white/85 rounded-xl border border-fuchsia-100 p-3 sm:p-4" data-testid="sample-posts">
            <div className="text-[11px] uppercase tracking-wider text-fuchsia-700 font-semibold mb-2.5">
              Latest in the Community
            </div>
            <div className="space-y-1.5">
              {data.sample_posts.slice(0, 3).map((p) => (
                <button
                  key={p.id}
                  onClick={() => navigate('/community')}
                  className="w-full text-left flex items-start gap-3 px-2.5 py-2 rounded-lg hover:bg-fuchsia-50 transition group"
                  data-testid={`highlight-post-${p.id}`}
                >
                  <span className="text-xl shrink-0 leading-none mt-0.5">
                    {RECOGNITION_KIND_ICONS[p.kind] || '✨'}
                  </span>
                  <FeedPostContent
                    content={p.content || ''}
                    className="text-xs sm:text-sm text-slate-700 flex-1 group-hover:text-slate-900 line-clamp-2 leading-snug"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


/**
 * Individual KPI tile.
 *
 * Layout:
 *   row 1: icon chip (own line)
 *   row 2: label (own line, can wrap to 2 lines without collision)
 *   row 3: value (big number)
 *   row 4: sub (truncated if too long)
 *
 * `min-h-[112px]` reserves space so birthday-vs-cert tiles stay aligned
 * even when one has a sub-text and the other doesn't.
 */
function KpiTile({ icon, label, value, sub, color, testid }) {
  const palette = {
    fuchsia: { bg: 'bg-fuchsia-100/60', border: 'border-fuchsia-200', icon: 'text-fuchsia-700', value: 'text-fuchsia-900' },
    pink:    { bg: 'bg-pink-100/60',    border: 'border-pink-200',    icon: 'text-pink-700',    value: 'text-pink-900'    },
    emerald: { bg: 'bg-emerald-100/60', border: 'border-emerald-200', icon: 'text-emerald-700', value: 'text-emerald-900' },
    amber:   { bg: 'bg-amber-100/60',   border: 'border-amber-200',   icon: 'text-amber-700',   value: 'text-amber-900'   },
  };
  const c = palette[color] || palette.fuchsia;
  return (
    <div
      className={`rounded-xl border ${c.border} bg-white p-3 sm:p-3.5 min-h-[112px] flex flex-col overflow-hidden min-w-0`}
      data-testid={testid}
    >
      <div className={`inline-flex w-7 h-7 items-center justify-center rounded-md ${c.bg} ${c.icon} shrink-0`}>
        {icon}
      </div>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mt-2 leading-tight truncate">
        {label}
      </div>
      <div className={`text-2xl font-bold mt-auto ${c.value} truncate`}>
        {value || 0}
      </div>
      {sub && (
        <div className="text-[11px] text-slate-500 truncate mt-0.5" title={sub}>
          {sub}
        </div>
      )}
    </div>
  );
}
